import React from "react";
import { BrowserRouter as Router, Navigate, Route, Routes } from "react-router-dom";
import { useGaPageView } from "@/hooks/useGaPageView";

// Layouts
import AppLayout from "@/components/layout/AppLayout";
import LandingLayout from "@/components/layout/LandingLayout";
import AuthLayout from "@/components/layout/AuthLayout";

// Guards
import { AuthGuard } from "@/routes/guards/AuthGuard";
import { GuestGuard } from "@/routes/guards/GuestGuard";
import FeatureGuard from "@/routes/guards/FeatureGuard";

// Context Providers
import { FeatureAccessProvider } from "@/contexts/FeatureAccessContext";

// Pages
import Dashboard from "@/pages/app/Dashboard.tsx";
import Search from "@/pages/app/Search.tsx";
import Saved from "@/pages/app/Saved.tsx";
import Settings from "@/pages/app/Settings.tsx";
import ThreatProfile from "@/pages/app/ThreatProfile.tsx";
import WatchlistPage from "@/pages/app/WatchlistPage.tsx";
import ThreatFeedPage from "@/pages/app/threat-feeds";
import TelegramDashboard from '@/pages/app/TelegramDashboard.tsx';
import Reports from "@/pages/app/Reports";

// Landing Page Components
import LandingPricing from "@/pages/landing/LandingPricing";
import LandingContact from "@/pages/landing/LandingContact";

// Auth Components
import LoginPage from "@/pages/auth/LoginPage";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "../pages/auth/ResetPassword.tsx";
import AuthSuccess from "../pages/auth/AuthSuccess.tsx";
import AcceptInvitation from "../pages/auth/AcceptInvitation.tsx";
import Homapage from "@/pages/landing/home.tsx";
import LongformPage from "@/pages/landing/longform.tsx";

const GaPageViewTracker = () => {
    useGaPageView();
    return null;
};

const AppRoutes: React.FC = () => {
    return (
        <Router>
            <GaPageViewTracker />
            <Routes>
                {/* Landing Pages (Public - No Guards) */}
                <Route path="/" element={<LandingLayout />}>
                    <Route index element={<Homapage />} />
                    <Route path="longform" element={<LongformPage />} />
                    <Route path="pricing" element={<LandingPricing />} />
                    <Route path="contact" element={<LandingContact />} />
                </Route>

                {/* Authentication Pages (Guest Only) */}
                <Route
                    path="/auth"
                    element={
                        <GuestGuard>
                            <AuthLayout />
                        </GuestGuard>
                    }
                >
                    <Route index element={<Navigate to="/auth/login" replace />} />
                    <Route path="login" element={<LoginPage />} />
                    <Route path="forgot-password" element={<ForgotPassword />} />
                    <Route path="reset-password" element={<ResetPassword />} />
                    <Route path="accept-invitation" element={<AcceptInvitation />} />
                    <Route path="success" element={<AuthSuccess />} />
                </Route>

                {/* Protected App Layout (Auth Required) */}
                <Route
                    path="/app"
                    element={
                        <AuthGuard>
                            <FeatureAccessProvider>
                                <AppLayout />
                            </FeatureAccessProvider>
                        </AuthGuard>
                    }
                >
                    {/* Dashboard - Always accessible */}
                    <Route index element={<Dashboard />} />
                    <Route path="dashboard" element={<Navigate to="/app" replace />} />
                    <Route path="/app/dashboard" element={<Dashboard />} />

                    {/* 
                    Telegram Intelligence - Security clearance handled internally via TelegramDashboard 
                    */}
                    <Route
                        path="forums"
                        element={<TelegramDashboard />}
                    />

                    {/* 
                    Search Module - Requires email records feature 
                    */}
                    <Route
                        path="search"
                        element={
                            <FeatureGuard requiredFeature="emailRecords" pageId="search">
                                <Search />
                            </FeatureGuard>
                        }
                    />

                    {/* 
                    Threat Feed - Requires threat feeds feature 
                    */}
                    <Route
                        path="threat-feed"
                        element={
                            <FeatureGuard requiredFeature="threatFeeds" pageId="threat-feed">
                                <ThreatFeedPage />
                            </FeatureGuard>
                        }
                    />

                    {/* 
                    Reports - Requires threat profiling feature (matches our Sidebar setup) 
                    */}
                    <Route
                        path="reports"
                        element={
                            <FeatureGuard requiredFeature="threatProfiling" pageId="reports">
                                <Reports />
                            </FeatureGuard>
                        }
                    />

                    {/* 
                    Threat Profile - Requires threat profiling feature 
                    */}
                    <Route
                        path="threat-profile"
                        element={
                            <FeatureGuard requiredFeature="threatProfiling" pageId="threat-profile">
                                <ThreatProfile />
                            </FeatureGuard>
                        }
                    />

                    {/* 
                    Saved Incidents - Requires incident saving feature 
                    */}
                    <Route
                        path="saved"
                        element={
                            <FeatureGuard requiredFeature="incidentSaving" pageId="saved">
                                <Saved />
                            </FeatureGuard>
                        }
                    />

                    {/* 
                    Watchlist - Requires real-time alerting feature 
                    */}
                    <Route
                        path="watchlist"
                        element={
                            <FeatureGuard requiredFeature="realTimeAlerting" pageId="watchlist">
                                <WatchlistPage />
                            </FeatureGuard>
                        }
                    />

                    {/* Settings - Always accessible for plan management */}
                    <Route path="settings" element={<Settings />} />

                    {/* Legacy routes redirect to unified settings */}
                    <Route path="settings/account" element={<Navigate to="/app/settings" replace />} />
                    <Route path="settings/organization" element={<Navigate to="/app/settings" replace />} />
                    <Route path="account" element={<Navigate to="/app/settings" replace />} />
                </Route>

                {/* Catch all redirect to landing */}
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </Router>
    );
};

export default AppRoutes;